import { Orientation3D, Position3D } from '@fixtuur/fine';
import { Context, Function, Effect, Either, Layer, Record } from 'effect';
import * as ApiAttachments from '../../api/aspect/attachments';
import { FileService } from '../../services/file';
import * as H from './handedness';
import { Models, ToastModelsService, } from '../../services/models';
import { emptyPose, poseString } from './pose';
import * as P from './pose';
/*
 * ========= Constructors
 */
export const emptyAttachments = () => {
    return {
        possibleHandedness: H.everyPossibleHandedness(),
        originDeltas: [emptyPose(H.everyPossibleHandedness())],
        attachments: {},
    };
};
export const fromRawPoses = (poses) => {
    const possibleHandedness = H.everyPossibleHandedness();
    const originDeltas = [emptyPose()];
    const attachments = Record.map(poses, Function.flow(P.fromRawPose, pose => [pose]));
    return {
        possibleHandedness,
        originDeltas,
        attachments,
    };
};
export const fromApi = (attachments) => {
    const conditions = attachments.conditions;
    const possibleHandedness = H.possibleHandednessFromApiAttachment(attachments);
    const attachment = {
        possibleHandedness,
        originDeltas: attachments.originDeltas.map(od => ({
            possibleHandedness: new Set(od.applicableConditions
                .map(id => conditions.find(x => x.id === id))
                .map(c => c?.orientation)
                .filter((x) => x !== undefined)).add('none'),
            position: new Position3D(od.position.x, od.position.y, od.position.z),
            orientation: Orientation3D.fromQuaternion([
                od.rotation.w,
                od.rotation.x,
                od.rotation.y,
                od.rotation.z,
            ]),
        })),
        attachments: Record.map(attachments.attachments, ats => ats.map(at => ({
            position: new Position3D(at.position.x, at.position.y, at.position.z),
            orientation: Orientation3D.fromQuaternion([
                at.rotation.w,
                at.rotation.x,
                at.rotation.y,
                at.rotation.z,
            ]),
            possibleHandedness: new Set(at.applicableConditions
                .map(id => conditions.find(x => x.id === id))
                .map(c => c?.orientation)
                .filter((x) => x !== undefined)).add('none'),
        }))),
    };
    return attachment;
};
export const fromApiJson = (json) => ApiAttachments.fromJson(json).pipe(Effect.map(fromApi));
export const fromApiJsonFile = (file) => Effect.gen(function* (_) {
    const fs = yield* _(FileService);
    const str = yield* _(fs.toString(file));
    const apiType = yield* _(ApiAttachments.fromJsonString(str));
    return fromApi(apiType);
});
/*
 * ========= Methods
 */
export const attachmentsString = (parameters) => {
    const possibleHandedness = H.possibleHandednessString(parameters.possibleHandedness);
    const originDeltas = parameters.originDeltas.map(poseString).join(',\n\t');
    const attachments = Object.values(Record.map(parameters.attachments, (poses, key) => key + ': ' + poses.map(poseString).join(',\n'))).join('\n\t');
    return `{
    possibleHandedness: ${possibleHandedness},
    originDeltas: ${originDeltas},
    attachments:
        ${attachments}
}`;
};
export const posesForHandedness = (existing, possibleHandedness) => {
    // is every handedness covered by some pose?
    const uncovered = Array.from(possibleHandedness.values()).filter(handedness => !existing.some(pose => pose.possibleHandedness.has(handedness)));
    if (uncovered.length === 0) {
        // we already cover every case
        return existing;
    }
    else {
        // ensure we cover the cases that are currently uncovered
        return existing.concat(uncovered.map(handedness => {
            return emptyPose(H.possibleHandednessFromHandedness(handedness));
        }));
    }
};
/* Adjust the Origin Deltas and Attachment data to ensure it is consistent with
 * the given PossibleHandedness values. */
export const normalise = (params) => {
    // when a handedness is supported, ensure  that there is an Origin Delta for it
    const originDeltas = posesForHandedness(params.originDeltas, params.possibleHandedness);
    const attachments = Record.map(params.attachments, poses => posesForHandedness(poses, params.possibleHandedness));
    return { ...params, originDeltas, attachments };
};
export class NoOriginDeltaError {
    candidates;
    _tag = 'Fixtuur/Aspect/Attachment/NoOriginDeltaError';
    constructor(candidates) {
        this.candidates = candidates;
    }
}
export class MissingAttachmentError {
    articleId;
    _tag = 'Fixtuur/Aspect/Attachment/MissingAttachmentError';
    constructor(articleId) {
        this.articleId = articleId;
    }
}
const valuesDefined = (obj) => Object.values(obj).every(x => x !== undefined);
export const narrow = (attachments, handedness) => {
    const originDelta = attachments.originDeltas.find(od => od.possibleHandedness.has(handedness)) ?? emptyPose();
    const attachmentsForHandedness = Record.map(attachments.attachments, ats => ats.find(at => at.possibleHandedness.has(handedness)));
    if (!valuesDefined(attachmentsForHandedness)) {
        return Either.left(new MissingAttachmentError(Object.keys(attachmentsForHandedness).join(', ')));
    }
    return Either.right({
        handedness,
        originDelta: originDelta,
        attachments: attachmentsForHandedness,
    });
};
export const possibleHandedness = (attachments) => H.intersectionMonoid.combineAll(attachments.map(a => a.possibleHandedness));
export const get = (tenant, productId, articleId) => Effect.andThen(Models, m => m.attachmentsForArticle(tenant, productId, articleId)).pipe(Effect.map(fromApi));
export class FixtuurAspectAttachmentsService extends Context.Tag('@fixtuur/backpack/FixtuurAspectAttachmentsService')() {
}
export const ToastFixtuurAspectAttachmentsService = Layer.effect(FixtuurAspectAttachmentsService, Effect.andThen(Models, m => {
    const get = (t, p, a) => m.attachmentsForArticle(t, p, a).pipe(Effect.map(fromApi));
    return { get };
})).pipe(Layer.provide(ToastModelsService));
